import { useTranslation } from "react-i18next";
import { Icon, Button } from "@thetreep/ui";
import { useLocation, useNavigate } from "react-router";
import { useToaster } from "@hooks/useToaster";
import { useState } from "react";
import {
  Price,
  ProductPriceSchema,
  Travel,
  TravelSchema,
} from "@gen/travel/v1/common_pb";
import { Solution } from "@gen/travel/v1/search_pb";
import { useExchangeContext } from "./context";
import { ExchangeHeader } from "./ExchangeHeader";
import { Place } from "@gen/place/v1/place_pb";
import { format } from "date-fns/format";
import i18n from "@libs/i18n";
import JourneyDisplay from "../cart/journey/Journey";
import { confirmExchange } from "@services/exchange";
import { routes } from "@routes";
import { ConnectError } from "@connectrpc/connect";
import { ExchangeOffer } from "@gen/travel/v1/exchange_pb";
import { create } from "@bufbuild/protobuf";
import { timestampDate } from "@bufbuild/protobuf/wkt";

export function ExchangeRecap() {
  // deps
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // params
  const { travel, journey } = useExchangeContext();
  const { addToast } = useToaster();
  const [offer, setOffer] = useState<ExchangeOffer>(location.state?.offer);
  const [solution, setSolution] = useState<Solution>(location.state?.solution);
  const origin = solution?.journey?.route?.origin?.name;
  const destination = solution?.journey?.route?.destination?.name;
  const confirmationTimeLimit = travel?.confirmationDeadline
    ? timestampDate(travel.confirmationDeadline)
    : undefined;

  // state
  const [isSubmitting, setIsSubmitting] = useState(false);

  function displayCity(place: Place | undefined) {
    if (!place) return "";
    return place.city || place.name;
  }

  function formatPrice(price: Price | undefined) {
    if (!price) return "-";
    const formatter = new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: price?.currency || "EUR",
    });
    return formatter.format(Number(price.amount) / 100);
  }

  async function handleValidate() {
    if (!offer) throw "offer is undefined";
    setIsSubmitting(true);
    try {
      const res = await confirmExchange(travel.id, journey.id, offer.id);
      navigate(routes.CONFIRMATION, {
        state: { travel: res.travel, context: "exchange" },
      });
    } catch (e: unknown) {
      console.error(e);
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function overrideTravelWithOffer(travel: Travel, offer: ExchangeOffer) {
    return create(TravelSchema, {
      ...travel,
      initialPrices: [
        create(ProductPriceSchema, {
          productIds: [offer.products[0].product?.id || ""],
          price: offer.newPrice,
        }),
      ],
      products: offer.products.map((p) => p.product).filter((p) => !!p),
    });
  }

  const [prevLocation, setPrevLocation] = useState(location);
  if (location !== prevLocation) {
    setPrevLocation(location);
    setOffer(location.state.offer);
    setSolution(location.state.solution);
  }

  if (
    !offer ||
    !solution ||
    !travel ||
    !journey ||
    !solution.journey?.schedule?.departure
  )
    return;

  return (
    <div className="flex flex-col gap-10 mb-20 lg:mb-0">
      <div className="flex flex-col gap-1">
        <h1 className="text-[24px] lg:text-[32px] text-strong font-bold font-jakarta">
          {t("exchange.exchange")}: {origin} → {destination}
        </h1>
      </div>

      <ExchangeHeader />

      <div className="flex flex-col gap-14">
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="bg-white rounded-lg p-6 gap-6 lg:p-8 lg:gap-10 w-full">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 lg:flex-row lg:justify-between">
                <h2 className="font-jakarta text-[14px] font-bold lg:text-[20px] text-text-strong">
                  {`${t("exchange.newTravel")} ${displayCity(
                    solution.journey?.route?.origin,
                  )} → ${displayCity(solution.journey?.route?.destination)}`}
                  <br />
                </h2>
                {confirmationTimeLimit && (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[14px] text-text-soft">
                      {t("cart.guaranteedUpTo")}{" "}
                      {format(
                        new Date(confirmationTimeLimit),
                        `dd/MM '${t("cart.at")}' HH'${t(
                          "cart.hoursMinutesSeparator",
                        )}'mm`,
                      )}
                    </p>
                  </div>
                )}
              </div>

              <JourneyDisplay
                travel={overrideTravelWithOffer(travel, offer)}
                journey={solution.journey}
              />
            </div>
            <div className="flex flex-col gap-4 mt-4 text-text-strong">
              <div className="flex text-base font-bold lg:text-[24px] font-jakarta justify-between">
                {t("exchange.toPay")} :{" "}
                <span>{formatPrice(offer.outOfPocketAmount)}</span>
              </div>
              <div className="flex flex-col gap-1">
                <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
                  {t("exchange.priceDifference")}
                  <span>{formatPrice(offer.priceDelta)}</span>
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
                  {t("exchange.exchangeFees")}
                  <span>{formatPrice(offer.exchangeFees)}</span>
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
                  {t("exchange.agencyFees")}
                  <span>{formatPrice(undefined)}</span>
                </p>
              </div>
              <div className="flex lg:justify-end [&_button]:w-full lg:[&_button]:w-auto">
                <Button
                  variant="primary"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  onClick={handleValidate}
                  endEnhancer={<Icon src="arrow_right_line" color="white" />}
                >
                  {t("exchange.confirm")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
